import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { EventBookingEvent } from '../types/eventBooking';
import type { PaginatedResponse } from '../types/api';

type EventBookingEventsResponse = PaginatedResponse<EventBookingEvent[]>;
type DefaultError = unknown;

type UseEventBookingEventsParams = {
  pageNumber?: number;
  pageSize?: number;
  include?: string;
  filters?: Record<string, string>;
  enabled?: boolean;
  [key: string]:
    | string
    | number
    | boolean
    | undefined
    | null
    | Record<string, string>;
};

export function useEventBookingEvents<
  TData = EventBookingEventsResponse,
  TError = DefaultError,
>(
  {
    pageNumber = 1,
    pageSize = 10,
    include,
    filters,
    enabled = true,
    ...restParams
  }: UseEventBookingEventsParams = {},
  options?: Omit<
    UseQueryOptions<EventBookingEventsResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = [
    'eventBookingEvents',
    pageNumber,
    pageSize,
    include,
    filters,
    restParams,
  ];

  return useQuery<EventBookingEventsResponse, TError, TData>({
    queryKey,
    enabled,
    queryFn: async () =>
      service.getEventBookingEvents({
        pageNumber,
        pageSize,
        ...(include ? { include } : {}),
        ...(filters || {}),
        ...(restParams ? { ...restParams } : {}),
      }),
    ...(options as any),
  });
}
